import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPKR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Download, FileSpreadsheet, FileArchive, Loader2, CheckCircle2,
  AlertTriangle, XCircle, Images, RotateCcw, Upload,
} from "lucide-react";

const resolveImage = (url) => {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const downloadBlob = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const StepHead = ({ n, title, hint }) => (
  <div className="mb-4">
    <div className="flex items-center gap-3">
      <span className="w-7 h-7 flex items-center justify-center bg-sage text-cream text-xs font-semibold shrink-0">{n}</span>
      <h2 className="font-display text-2xl text-ink leading-none">{title}</h2>
    </div>
    {hint && <p className="text-sm text-ink-soft mt-2 ml-10 leading-relaxed">{hint}</p>}
  </div>
);

const Card = ({ children, testid }) => (
  <section className="bg-white border border-border-subtle p-6 lg:p-8 mb-6" data-testid={testid}>
    {children}
  </section>
);

function DropZone({ accept, file, onFile, icon: Icon, label, hint, testid }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      onClick={() => ref.current?.click()}
      className={`cursor-pointer border-2 border-dashed transition-colors py-10 px-6 flex flex-col items-center gap-2 text-center ${
        over ? "border-sage bg-cream-warm" : file ? "border-sage/60 bg-cream-warm/50" : "border-border-subtle hover:border-sage hover:bg-cream-warm/40"
      }`}
      data-testid={testid}
    >
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
        data-testid={`${testid}-input`}
      />
      <Icon size={22} className={file ? "text-sage" : "text-ink-soft"} />
      {file ? (
        <>
          <span className="text-sm font-semibold text-ink">{file.name}</span>
          <span className="text-xs text-ink-soft">{(file.size / 1048576).toFixed(2)} MB — click to replace</span>
        </>
      ) : (
        <>
          <span className="text-sm font-semibold uppercase tracking-widest text-ink">{label}</span>
          <span className="text-xs text-ink-soft">{hint}</span>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, tone = "ink", testid }) => {
  const tones = {
    ink: "text-ink",
    sage: "text-sage-dark",
    rose: "text-rose-dark",
    amber: "text-amber-600",
  };
  return (
    <div className="bg-cream-warm border border-border-subtle p-4" data-testid={testid}>
      <div className={`font-display text-3xl ${tones[tone]}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mt-1">{label}</div>
    </div>
  );
};

export default function AdminBulkImport() {
  const [csvFile, setCsvFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inspect, setInspect] = useState(null);
  const timer = useRef(null);

  const status = session?.status;
  const summary = session?.summary;
  const rows = session?.rows || [];
  const results = session?.results;

  const poll = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/admin/import/${id}`);
      setSession(data);
      if (data.status === "validating" || data.status === "importing") {
        timer.current = setTimeout(() => poll(id), 1200);
      }
    } catch {
      /* stop polling on error */
    }
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const startValidation = async () => {
    if (!csvFile) { toast.error("Upload a products CSV first"); return; }
    setStarting(true);
    try {
      const fd = new FormData();
      fd.append("csv_file", csvFile);
      if (zipFile) fd.append("zip_file", zipFile);
      const { data } = await api.post("/admin/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await poll(data.session_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the uploaded files");
    } finally {
      setStarting(false);
    }
  };

  const patchRow = async (rowNumber, patch) => {
    try {
      const { data } = await api.patch(`/admin/import/${session.id}/rows/${rowNumber}`, patch);
      setSession(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update this row");
    }
  };

  const commit = async () => {
    setConfirmOpen(false);
    try {
      const fd = new FormData();
      fd.append("mode", updateMode ? "update" : "skip");
      await api.post(`/admin/import/${session.id}/commit`, fd);
      poll(session.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import could not be started");
    }
  };

  const reset = async () => {
    clearTimeout(timer.current);
    if (session && session.status !== "completed") {
      try { await api.delete(`/admin/import/${session.id}`); } catch { /* ignore */ }
    }
    setSession(null);
    setCsvFile(null);
    setZipFile(null);
  };

  const pct = session?.progress?.total
    ? Math.round((session.progress.done / session.progress.total) * 100)
    : 0;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="admin-bulk-import-page">
      <Link to="/admin/products" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-ink-soft hover:text-sage mb-4" data-testid="back-to-products-link">
        <ArrowLeft size={13} /> Back to Products
      </Link>
      <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
        <div>
          <div className="label-eyebrow mb-1">Catalog</div>
          <h1 className="font-display text-4xl text-ink">Bulk Import</h1>
          <p className="text-sm text-ink-soft mt-2 max-w-xl leading-relaxed">
            Add up to 50 products at a time with a spreadsheet and a folder of images.
            Nothing is published until you press <span className="text-ink font-semibold">Import Products</span>.
          </p>
        </div>
        {session && (
          <Button variant="outline" onClick={reset} data-testid="start-over-btn">
            <RotateCcw size={14} className="mr-1.5" /> Start Over
          </Button>
        )}
      </div>

      {/* ---------- Step 1: template ---------- */}
      <Card testid="step-template">
        <StepHead n={1} title="Download the CSV template" hint="One row per product. Keep the column headings exactly as they are — delete the two example rows before importing." />
        <div className="flex flex-wrap gap-3 ml-0 lg:ml-10">
          <Button
            onClick={() => downloadBlob("/admin/import/template.csv", "bedding-lounge-product-template.csv").catch(() => toast.error("Download failed"))}
            className="bg-sage hover:bg-sage-dark text-cream uppercase tracking-widest text-xs"
            data-testid="download-template-btn"
          >
            <Download size={14} className="mr-1.5" /> Product CSV Template
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadBlob("/admin/import/instructions.csv", "bedding-lounge-csv-instructions.csv").catch(() => toast.error("Download failed"))}
            className="uppercase tracking-widest text-xs"
            data-testid="download-instructions-btn"
          >
            <Download size={14} className="mr-1.5" /> Column Instructions
          </Button>
        </div>
      </Card>

      {/* ---------- Steps 2 & 3: uploads ---------- */}
      {!session && (
        <>
          <Card testid="step-csv">
            <StepHead n={2} title="Upload your products CSV" hint="Every product needs a unique SKU — that SKU also names its image folder." />
            <div className="ml-0 lg:ml-10">
              <DropZone
                accept=".csv,text/csv"
                file={csvFile}
                onFile={(f) => {
                  if (!f.name.toLowerCase().endsWith(".csv")) { toast.error("Please choose a .csv file"); return; }
                  setCsvFile(f);
                }}
                icon={FileSpreadsheet}
                label="Drop your CSV here or click to choose"
                hint="Only .csv files — up to 200 rows"
                testid="csv-dropzone"
              />
            </div>
          </Card>

          <Card testid="step-zip">
            <StepHead
              n={3}
              title="Upload your product images ZIP (optional)"
              hint="Inside the ZIP, create one folder per SKU. Images are used in filename order — the first file becomes the main product image."
            />
            <div className="ml-0 lg:ml-10">
              <div className="bg-cream-warm border border-border-subtle p-4 mb-4 font-mono text-[11px] leading-relaxed text-ink-soft" data-testid="zip-structure-help">
                Product-Images.zip<br />
                &nbsp;&nbsp;BL-001/<br />
                &nbsp;&nbsp;&nbsp;&nbsp;01-main.webp&nbsp;&nbsp;<span className="text-sage-dark">← main image</span><br />
                &nbsp;&nbsp;&nbsp;&nbsp;02-top-view.webp<br />
                &nbsp;&nbsp;&nbsp;&nbsp;03-pillow-closeup.webp<br />
                &nbsp;&nbsp;BL-002/<br />
                &nbsp;&nbsp;&nbsp;&nbsp;01-main.jpg
              </div>
              <DropZone
                accept=".zip,application/zip"
                file={zipFile}
                onFile={(f) => {
                  if (!f.name.toLowerCase().endsWith(".zip")) { toast.error("Please choose a .zip file"); return; }
                  setZipFile(f);
                }}
                icon={FileArchive}
                label="Drop your images ZIP here or click to choose"
                hint="JPG, PNG, WEBP or GIF — up to 6 MB per image, 80 MB per ZIP"
                testid="zip-dropzone"
              />
            </div>
          </Card>

          <Card testid="step-validate">
            <StepHead n={4} title="Check the files" hint="We read everything and show you a preview first. No products are created in this step." />
            <div className="ml-0 lg:ml-10">
              <Button
                onClick={startValidation}
                disabled={!csvFile || starting}
                className="bg-ink hover:bg-ink/90 text-cream uppercase tracking-widest text-xs"
                data-testid="validate-btn"
              >
                {starting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Reading files…</> : <><Upload size={14} className="mr-1.5" /> Validate &amp; Preview</>}
              </Button>
            </div>
          </Card>
        </>
      )}

      {/* ---------- Progress ---------- */}
      {(status === "validating" || status === "importing") && (
        <Card testid="import-progress">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 size={18} className="animate-spin text-sage" />
            <div className="font-display text-2xl text-ink">
              {session.progress?.phase || (status === "validating" ? "Reading files" : "Importing")}…
            </div>
          </div>
          <div className="h-2 bg-cream-warm border border-border-subtle overflow-hidden">
            <div className="h-full bg-sage transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-xs text-ink-soft mt-2" data-testid="progress-text">
            {session.progress?.total ? `${session.progress.done} of ${session.progress.total}` : "Please wait"}
            {" — keep this page open."}
          </div>
        </Card>
      )}

      {/* ---------- File-level failures ---------- */}
      {status === "failed" && (
        <Card testid="import-failed">
          <div className="flex items-start gap-3">
            <XCircle size={20} className="text-rose-dark mt-0.5 shrink-0" />
            <div>
              <div className="font-display text-2xl text-ink mb-2">We couldn't read that file</div>
              <ul className="list-disc ml-5 space-y-1 text-sm text-ink-soft">
                {(session.file_errors || []).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
              <Button variant="outline" onClick={reset} className="mt-4 uppercase tracking-widest text-xs">Try again</Button>
            </div>
          </div>
        </Card>
      )}

      {/* ---------- Preview ---------- */}
      {status === "ready" && summary && (
        <>
          <Card testid="import-summary">
            <StepHead n={5} title="Review before importing" hint="Edit a name, price or stock right here. For anything else, fix your CSV and upload it again." />
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
              <Stat label="Products detected" value={summary.total} testid="stat-total" />
              <Stat label="Ready to import" value={summary.ready} tone="sage" testid="stat-ready" />
              <Stat label="With errors" value={summary.with_errors} tone={summary.with_errors ? "rose" : "ink"} testid="stat-errors" />
              <Stat label="Existing duplicates" value={summary.duplicates} tone={summary.duplicates ? "amber" : "ink"} testid="stat-duplicates" />
              <Stat label="Missing image folders" value={summary.missing_images} tone={summary.missing_images ? "amber" : "ink"} testid="stat-missing-images" />
            </div>

            {(session.zip_errors?.length > 0 || session.unmatched_folders?.length > 0) && (
              <div className="bg-amber-50 border border-amber-200 p-4 mb-6" data-testid="zip-issues">
                <div className="flex items-center gap-2 text-amber-800 text-xs uppercase tracking-widest font-semibold mb-2">
                  <AlertTriangle size={14} /> Image ZIP notes
                </div>
                <ul className="list-disc ml-5 space-y-1 text-sm text-amber-900">
                  {session.unmatched_folders?.map((f) => (
                    <li key={f}>Folder <span className="font-mono">{f}</span> doesn't match any SKU in the CSV — its images were ignored.</li>
                  ))}
                  {session.zip_errors?.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            <div className="border border-border-subtle overflow-x-auto">
              <table className="w-full min-w-[920px]">
                <thead className="bg-cream-warm border-b border-border-subtle">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-ink-soft">
                    <th className="p-3">Row</th>
                    <th className="p-3">Product</th>
                    <th className="p-3">SKU</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Base Price</th>
                    <th className="p-3">Stock</th>
                    <th className="p-3">Images</th>
                    <th className="p-3">New Arrivals</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const bad = r.errors?.length > 0;
                    return (
                      <tr key={r.row_number} className={`border-b border-border-subtle last:border-0 align-top ${bad ? "bg-rose/5" : ""}`} data-testid={`preview-row-${r.row_number}`}>
                        <td className="p-3 text-ink-soft text-xs">{r.row_number}</td>
                        <td className="p-3">
                          <div className="flex items-start gap-3">
                            <button
                              type="button"
                              onClick={() => setInspect(r)}
                              className="w-12 h-12 bg-cream-warm border border-border-subtle shrink-0 overflow-hidden flex items-center justify-center text-ink-soft"
                              data-testid={`inspect-row-${r.row_number}`}
                            >
                              {r.images?.[0]
                                ? <img src={resolveImage(r.images[0])} alt="" className="w-full h-full object-cover" />
                                : <Images size={14} />}
                            </button>
                            <div className="min-w-[180px]">
                              <Input
                                defaultValue={r.name}
                                onBlur={(e) => e.target.value !== r.name && patchRow(r.row_number, { name: e.target.value })}
                                className="h-8 text-sm bg-white"
                                data-testid={`row-name-${r.row_number}`}
                              />
                              {r.sub_collection_label && <div className="text-[11px] text-ink-soft mt-1">{r.sub_collection_label}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-xs font-mono text-ink">{r.sku || "—"}</td>
                        <td className="p-3 text-xs text-ink-soft">{r.category_label || "—"}</td>
                        <td className="p-3">
                          <Input
                            type="number"
                            defaultValue={r.price ?? ""}
                            onBlur={(e) => Number(e.target.value) !== r.price && patchRow(r.row_number, { price: Number(e.target.value) })}
                            className="h-8 w-28 text-sm bg-white"
                            data-testid={`row-price-${r.row_number}`}
                          />
                          {r.sale_price > 0 && (
                            <div className="text-[11px] text-rose-dark mt-1">Sale {formatPKR(r.sale_price)}</div>
                          )}
                          {Object.keys(r.variant_prices || {}).length > 0 && (
                            <div className="text-[11px] text-ink-soft mt-1">
                              {Object.keys(r.variant_prices).length} variant price(s)
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <Input
                            type="number"
                            defaultValue={r.stock ?? ""}
                            placeholder="—"
                            onBlur={(e) => {
                              const v = e.target.value;
                              if (v === "" || Number(v) === r.stock) return;
                              patchRow(r.row_number, { stock: Number(v) });
                            }}
                            className="h-8 w-20 text-sm bg-white"
                            data-testid={`row-stock-${r.row_number}`}
                          />
                        </td>
                        <td className="p-3 text-xs text-ink-soft" data-testid={`row-images-${r.row_number}`}>{r.image_count}</td>
                        <td className="p-3 text-xs text-ink-soft">{r.featured ? "Yes" : "No"}</td>
                        <td className="p-3 min-w-[240px]">
                          {bad ? (
                            <div className="space-y-1" data-testid={`row-errors-${r.row_number}`}>
                              {r.errors.map((e, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-[11px] text-rose-dark">
                                  <XCircle size={12} className="mt-0.5 shrink-0" /> <span>{e}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-[11px] text-sage-dark" data-testid={`row-ok-${r.row_number}`}>
                              <CheckCircle2 size={12} /> {r.duplicate ? "Existing SKU" : "Ready"}
                            </div>
                          )}
                          {r.warnings?.map((w, i) => (
                            <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700 mt-1">
                              <AlertTriangle size={12} className="mt-0.5 shrink-0" /> <span>{w}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {summary.with_errors > 0 && (
              <Button
                variant="outline"
                onClick={() => downloadBlob(`/admin/import/${session.id}/errors.csv`, "import-errors.csv").catch(() => toast.error("Download failed"))}
                className="mt-4 uppercase tracking-widest text-xs"
                data-testid="download-errors-btn"
              >
                <Download size={14} className="mr-1.5" /> Download Error Report
              </Button>
            )}
          </Card>

          <Card testid="step-confirm">
            <StepHead n={6} title="Import the products" hint="Rows with errors are left out. Nothing else in your catalogue is touched." />
            <div className="ml-0 lg:ml-10">
              <div className="flex items-start gap-3 bg-cream-warm border border-border-subtle p-4 mb-5">
                <Switch checked={updateMode} onCheckedChange={setUpdateMode} data-testid="update-mode-toggle" />
                <div>
                  <div className="text-sm text-ink">Update existing products ({summary.duplicates} matched by SKU)</div>
                  <div className="text-[11px] text-ink-soft leading-relaxed mt-0.5">
                    Off (default): products whose SKU already exists are skipped and left untouched.
                    On: their details are updated — blank cells keep the current value, and a Stock of 0 is applied as a real change.
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={summary.ready === 0}
                  className="bg-sage hover:bg-sage-dark text-cream uppercase tracking-widest text-xs"
                  data-testid="commit-import-btn"
                >
                  <CheckCircle2 size={14} className="mr-1.5" /> Import {summary.ready} Product{summary.ready === 1 ? "" : "s"}
                </Button>
                <span className="text-xs text-ink-soft">Stock values are manual — customer orders never change them.</span>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* ---------- Results ---------- */}
      {status === "completed" && results && (
        <Card testid="import-results">
          <div className="flex items-center gap-3 mb-5">
            <CheckCircle2 size={22} className="text-sage" />
            <h2 className="font-display text-3xl text-ink">Import complete</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <Stat label="Created" value={results.created.length} tone="sage" testid="result-created" />
            <Stat label="Updated" value={results.updated.length} tone="sage" testid="result-updated" />
            <Stat label="Skipped duplicates" value={results.skipped.length} tone="amber" testid="result-skipped" />
            <Stat label="Failed" value={results.failed.length} tone={results.failed.length ? "rose" : "ink"} testid="result-failed" />
            <Stat label="Images uploaded" value={results.images} testid="result-images" />
          </div>

          {[["Created", results.created, "sage"], ["Updated", results.updated, "sage"]].map(([title, list]) => list.length > 0 && (
            <div key={title} className="mb-5">
              <div className="label-eyebrow !text-[10px] mb-2">{title}</div>
              <ul className="text-sm text-ink-soft space-y-1">
                {list.map((p) => (
                  <li key={p.id}>
                    <span className="font-mono text-xs text-ink mr-2">{p.sku}</span>{p.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {results.skipped.length > 0 && (
            <div className="mb-5">
              <div className="label-eyebrow !text-[10px] mb-2">Skipped</div>
              <ul className="text-sm text-ink-soft space-y-1">
                {results.skipped.map((p, i) => (
                  <li key={i}><span className="font-mono text-xs text-ink mr-2">{p.sku}</span>{p.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {results.failed.length > 0 && (
            <div className="mb-5 bg-rose/5 border border-rose/30 p-4">
              <div className="label-eyebrow !text-[10px] mb-2 text-rose-dark">Failed</div>
              <ul className="text-sm text-rose-dark space-y-1">
                {results.failed.map((p, i) => (
                  <li key={i}><span className="font-mono text-xs mr-2">{p.sku}</span>{p.error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2 border-t border-border-subtle">
            <Link to="/admin/products">
              <Button className="bg-ink hover:bg-ink/90 text-cream uppercase tracking-widest text-xs" data-testid="view-products-btn">
                View products in admin
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => downloadBlob(`/admin/import/${session.id}/errors.csv`, "import-errors.csv").catch(() => toast.error("Download failed"))}
              className="uppercase tracking-widest text-xs"
              data-testid="download-report-btn"
            >
              <Download size={14} className="mr-1.5" /> Download Error Report
            </Button>
            <Button variant="outline" onClick={reset} className="uppercase tracking-widest text-xs" data-testid="import-again-btn">
              <RotateCcw size={14} className="mr-1.5" /> Import another batch
            </Button>
          </div>
        </Card>
      )}

      {/* ---------- Inspect dialog ---------- */}
      <Dialog open={!!inspect} onOpenChange={(v) => !v && setInspect(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="inspect-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-ink">{inspect?.name}</DialogTitle>
            <DialogDescription className="text-ink-soft text-sm">
              Images, variant pricing and description read from your upload. Nothing here is live yet.
            </DialogDescription>
          </DialogHeader>
          {inspect && (
            <div className="space-y-5">
              <div className="text-xs text-ink-soft font-mono">{inspect.sku}</div>
              {inspect.images?.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {inspect.images.map((img, i) => (
                    <div key={i} className="relative aspect-square bg-cream-warm border border-border-subtle">
                      <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" />
                      {i === 0 && <span className="absolute top-1 left-1 bg-sage text-cream text-[9px] uppercase tracking-widest px-1.5 py-0.5">Main</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-ink-soft">No images matched this SKU.</div>
              )}
              {Object.keys(inspect.variant_prices || {}).length > 0 && (
                <div>
                  <div className="label-eyebrow !text-[10px] mb-2">Variant prices</div>
                  <ul className="text-sm text-ink-soft space-y-1">
                    {Object.entries(inspect.variant_prices).map(([k, v]) => (
                      <li key={k} className="flex justify-between border-b border-border-subtle pb-1">
                        <span className="font-mono text-xs">{k}</span>
                        <span>
                          {inspect.variant_sale_prices?.[k] ? (
                            <>
                              <span className="line-through text-ink-soft mr-2">{formatPKR(v)}</span>
                              <span className="text-rose-dark font-semibold">{formatPKR(inspect.variant_sale_prices[k])}</span>
                            </>
                          ) : formatPKR(v)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="label-eyebrow !text-[10px] mb-2">Description</div>
                <div className="prose prose-sm max-w-none text-ink-soft text-sm" data-testid="inspect-description">
                  {inspect.description
                    ? <div dangerouslySetInnerHTML={{ __html: inspect.description }} />
                    : <span>No description provided.</span>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white" data-testid="commit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-ink">
              Import {summary?.ready} product{summary?.ready === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-ink-soft">
              {updateMode
                ? `Existing products matched by SKU (${summary?.duplicates}) will be updated. Blank cells keep their current values.`
                : `Existing products matched by SKU (${summary?.duplicates}) will be skipped and left exactly as they are.`}
              {" "}Rows with errors are not imported.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="commit-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={commit} className="bg-sage hover:bg-sage-dark text-cream" data-testid="commit-confirm-btn">
              Yes, import now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
